import {
  GraphemeMorphemePanelsGateProvider,
  MorphemePanelsContextProvider,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { GraphemeMnemonicsMeaningPreviewSection } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics";
import {
  useMorphemeMnemonicsPanelMorphemeSliceQueryOptions,
  useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions,
} from "@-/dev.improve.haniverse.lib.morpheme.read";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import { MorphemeNotFound } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.not-found";
import { usePrefetchedSuspenseQueries } from "@-/com.npmjs.tanstack__react-query";
import { useRef } from "react";
import { View } from "@-/com.npmjs.react-native";

function noop() {
  // The preview renders the meaning tab only, so the panel-pager index this setter drives is
  // never read. `useMorphemePanelsContextValue` still requires one.
}

/**
 * The previewed grapheme's mnemonic meaning panel — the same component tree the real mnemonics
 * route renders, minus the pager chrome, the pronunciation tabs, and the edit affordance.
 *
 * Suspends on the two mnemonics slices. The sheet wraps this in its own boundary so a cold
 * grapheme shows a spinner inside the sheet rather than blanking it.
 */
export function GraphemePreviewSheetPanel({
  grapheme,
  height,
  morphemeHrefObject,
}: {
  grapheme: string;
  /**
   * The sheet body's resolved height. The meaning tab is a `flex-1` scroll container, so it
   * collapses to nothing without a bounded parent.
   */
  height: number;
  morphemeHrefObject: MorphemeHrefObject;
}) {
  const mnemonicsPanelMorphemeSliceQueryOptions =
    useMorphemeMnemonicsPanelMorphemeSliceQueryOptions(grapheme);
  const mnemonicsPanelMorphemeUserSliceQueryOptions =
    useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions(grapheme);
  const [{ data: readonlySlice }, { data: userSlice }] = usePrefetchedSuspenseQueries({
    queries: [mnemonicsPanelMorphemeSliceQueryOptions, mnemonicsPanelMorphemeUserSliceQueryOptions],
  });
  // The panel-pager navigation props are inert here (no pager, no toolbar), but the context value
  // requires them.
  const viewNavigationRef = useRef(null);

  if (readonlySlice === null) {
    return (
      <View style={{ height }}>
        <MorphemeNotFound morpheme={grapheme} />
      </View>
    );
  }

  return (
    <MorphemePanelsContextProvider
      activeIndex={0}
      character={grapheme}
      morphemeHrefObject={morphemeHrefObject}
      setActiveIndex={noop}
      viewNavigationRef={viewNavigationRef}
    >
      <GraphemeMorphemePanelsGateProvider
        readonlySlice={readonlySlice}
        readonlySliceQueryKey={mnemonicsPanelMorphemeSliceQueryOptions.queryKey}
        userSlice={userSlice}
        userSliceQueryKey={mnemonicsPanelMorphemeUserSliceQueryOptions.queryKey}
      >
        <View style={{ height }}>
          <GraphemeMnemonicsMeaningPreviewSection />
        </View>
      </GraphemeMorphemePanelsGateProvider>
    </MorphemePanelsContextProvider>
  );
}
